// src/components/resources/ebooks/EbookContentBlocks.tsx

import type { EbookContentBlock } from '~/lib/resources/types/ebooks/content-blocks'
import {
  LightBulbIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline'

interface EbookContentBlocksProps {
  blocks: EbookContentBlock[]
  /** Cap blocks shown on the landing page (full content is in the PDF) */
  limit?: number
}

const CALLOUT_STYLES = {
  tip: {
    icon: LightBulbIcon,
    label: 'Tip',
    className: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30',
    labelClass: 'text-emerald-700 dark:text-emerald-400',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    label: 'Warning',
    className: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30',
    labelClass: 'text-red-700 dark:text-red-400',
  },
  insight: {
    icon: SparklesIcon,
    label: 'Insight',
    className: 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30',
    labelClass: 'text-blue-700 dark:text-blue-400',
  },
  example: {
    icon: BeakerIcon,
    label: 'Example',
    className: 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30',
    labelClass: 'text-violet-700 dark:text-violet-400',
  },
} as const

function BlockPreview({ block }: { block: EbookContentBlock }): JSX.Element | null {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{block.text}</p>
    case 'callout': {
      const style = CALLOUT_STYLES[block.variant]
      const Icon = style.icon
      return (
        <div className={`rounded-xl border-l-4 border-l-current p-4 ${style.className}`}>
          <div className={`mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${style.labelClass}`}>
            <Icon className="h-4 w-4" aria-hidden="true" />
            {style.label}
          </div>
          {block.title && <p className="font-semibold text-gray-900 dark:text-white">{block.title}</p>}
          <p className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{block.body}</p>
        </div>
      )
    }
    case 'formula':
      return (
        <div className="rounded-xl bg-gray-900 p-4 text-white">
          <p className="text-xs font-bold uppercase tracking-wide text-blue-300">{block.label}</p>
          <p className="mt-2 font-mono text-sm font-semibold">{block.expression}</p>
          {block.note && <p className="mt-2 text-xs italic text-gray-400">{block.note}</p>}
        </div>
      )
    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          {block.caption && (
            <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-200">
              {block.caption}
            </p>
          )}
          <table className="min-w-full text-left text-xs">
            <thead>
              <tr className="bg-blue-600 text-white">
                {block.headers.map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.slice(0, 4).map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/40'}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 text-gray-600 dark:text-gray-300">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {block.rows.length > 4 && (
            <p className="px-3 py-2 text-xs text-gray-400">+ {block.rows.length - 4} more rows in the PDF</p>
          )}
        </div>
      )
    case 'steps':
      return (
        <ol className="space-y-2">
          {block.items.slice(0, 3).map((item, i) => (
            <li key={i} className="flex gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                {i + 1}
              </span>
              <span className="pt-0.5 leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
      )
    case 'checklist':
      return (
        <ul className="space-y-2">
          {block.items.slice(0, 4).map((item) => (
            <li key={item} className="flex gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-blue-500" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      )
    case 'scenario':
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
          <p className="font-semibold text-amber-900 dark:text-amber-200">{block.title}</p>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-amber-800 dark:text-amber-300">Context: </span>
            {block.context}
          </p>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-amber-800 dark:text-amber-300">Approach: </span>
            {block.approach}
          </p>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-amber-800 dark:text-amber-300">Outcome: </span>
            {block.outcome}
          </p>
        </div>
      )
    default:
      return null
  }
}

/**
 * Renders a preview of structured ebook blocks on the landing page.
 *
 * @param {EbookContentBlocksProps} props - Blocks and display limit
 * @returns {JSX.Element} Styled block preview list
 */
export function EbookContentBlocks({ blocks, limit = 3 }: EbookContentBlocksProps): JSX.Element {
  const visible = blocks.slice(0, limit)

  return (
    <div className="mt-4 space-y-4 border-t border-gray-100 pt-4 dark:border-gray-800">
      {visible.map((block, index) => (
        <BlockPreview key={index} block={block} />
      ))}
      {blocks.length > limit && (
        <p className="text-xs text-gray-400">
          + {blocks.length - limit} more sections in the full PDF
        </p>
      )}
    </div>
  )
}
